import { useEffect, useId, useRef, useState } from "react";
import { Alert, Button, DataTable, DetailList, Dialog, Field, Pagination, StatusBadge } from "../../../ui";
import type { DataColumn, FieldElement } from "../../../ui";
import { failure } from "../../workspace/behavior";
import type { Schemas } from "../../workspace/api.generated";
import { businessCodeLabel, date } from "../../workspace/model";
import { FilterBar, PageHeader, Panel, RowLink, StateNotice } from "../../workspace/shared";
import { GuidedActionButton } from "../../workspace/GuidedActionButton";
import { exportListFilterSchema, exportRequestSchema } from "../schema";
import type { ExportListCriteria, ExportRequestValues, ExportStatusFilter } from "../schema";

type QueryState = "idle" | "ready" | "loading" | "error" | "not-found";
type DetailQueryState = QueryState | "denied";
type RequestDialog = { readonly kind: "new" | "retry"; readonly source?: Schemas["DataExportJobView"] };
type DownloadError = { readonly job: Schemas["DataExportJobView"]; readonly message: string; readonly expired: boolean };

function exportStatus(status: string) {
  const normalized = status.toUpperCase();
  const labels: Record<string, string> = {
    REQUESTED: "요청 접수",
    PROCESSING: "파일 생성 중",
    READY: "다운로드 가능",
    FAILED: "생성 실패",
  };
  const variant = normalized === "READY" ? "success" : normalized === "FAILED" ? "danger" : normalized === "PROCESSING" ? "processing" : "waiting";
  return <StatusBadge variant={variant}>{labels[normalized] ?? normalized.replaceAll("_", " ")}</StatusBadge>;
}

function requestValues(job: Schemas["DataExportJobView"]): ExportRequestValues {
  return { from: job.from, to: job.to, reason: job.reason };
}

export interface ExportViewProps {
  readonly mode?: "combined" | "list" | "detail";
  readonly criteria: ExportListCriteria;
  readonly result?: Schemas["AdminPageDataExportJobView"];
  readonly listState: Exclude<QueryState, "idle" | "not-found">;
  readonly selectedJobId?: string;
  readonly selected?: Schemas["DataExportJobView"];
  readonly detailState: DetailQueryState;
  readonly pollTimedOut: boolean;
  readonly defaultRequest: ExportRequestValues;
  readonly notice?: string;
  readonly onSearch: (criteria: ExportListCriteria) => void;
  readonly onPageChange: (page: number) => void;
  readonly onSelect: (jobId: string) => void;
  readonly onClearSelection: () => void;
  readonly onRetryList: () => void | Promise<unknown>;
  readonly onRetryDetail: () => void;
  readonly onRequest: (values: ExportRequestValues) => Promise<Schemas["DataExportJobView"]>;
  readonly onDownload: (job: Schemas["DataExportJobView"]) => Promise<void>;
  readonly detailHref?: (jobId: string) => string;
  readonly onOpenDetail?: (jobId: string) => void;
  readonly backHref?: string;
  readonly backLabel?: string;
  readonly onBack?: () => void;
}

export function ExportView(props: ExportViewProps) {
  const mode = props.mode ?? "combined";
  const [filters, setFilters] = useState(() => ({
    status: props.criteria.status,
    exportType: props.criteria.exportType,
    requestedFrom: props.criteria.requestedFrom,
    requestedTo: props.criteria.requestedTo,
  }));
  const [filterErrors, setFilterErrors] = useState<Record<string, string>>({});
  const filterRefs = useRef<Record<string, FieldElement | null>>({});
  const [dialog, setDialog] = useState<RequestDialog>();
  const [draft, setDraft] = useState<ExportRequestValues>(props.defaultRequest);
  const [requestErrors, setRequestErrors] = useState<Record<string, string>>({});
  const requestRefs = useRef<Record<string, FieldElement | null>>({});
  const [requestPending, setRequestPending] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [requestBlocked, setRequestBlocked] = useState(false);
  const [downloadingJobId, setDownloadingJobId] = useState<string>();
  const [downloadedJobId, setDownloadedJobId] = useState("");
  const [downloadError, setDownloadError] = useState<DownloadError>();
  const formId = useId();

  useEffect(() => {
    setFilters({
      status: props.criteria.status,
      exportType: props.criteria.exportType,
      requestedFrom: props.criteria.requestedFrom,
      requestedTo: props.criteria.requestedTo,
    });
    setFilterErrors({});
  }, [props.criteria]);

  useEffect(() => {
    setDownloadError(undefined);
    setDownloadedJobId("");
  }, [props.selectedJobId]);

  const openRequest = (next: RequestDialog) => {
    setDialog(next);
    setDraft(next.source ? requestValues(next.source) : props.defaultRequest);
    setRequestErrors({}); setRequestError(""); setRequestBlocked(false);
  };
  const closeRequest = () => {
    if (requestPending) return;
    setDialog(undefined); setRequestErrors({}); setRequestError(""); setRequestBlocked(false);
  };
  const updateDraft = (key: keyof ExportRequestValues, value: string) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setRequestErrors((current) => ({ ...current, [key]: "" }));
  };
  const submitRequest = async () => {
    if (requestPending || requestBlocked) return;
    const parsed = exportRequestSchema.safeParse(draft);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] ??= issue.message;
      setRequestErrors(next);
      requestRefs.current[String(parsed.error.issues[0]?.path[0])]?.focus();
      return;
    }
    setRequestPending(true); setRequestError("");
    try {
      await props.onRequest(parsed.data);
      setDialog(undefined);
    } catch (cause) {
      const feedback = failure(cause);
      setRequestError(feedback.message);
      setRequestBlocked(feedback.reconcile);
    } finally {
      setRequestPending(false);
    }
  };
  const reconcileRequest = async () => {
    try {
      await props.onRetryList();
      setDialog(undefined); setRequestError(""); setRequestBlocked(false);
    } catch (cause) {
      setRequestError(failure(cause).message);
    }
  };

  const retryList = () => {
    void Promise.resolve(props.onRetryList()).catch(() => undefined);
  };

  const submitFilters = () => {
    const parsed = exportListFilterSchema.safeParse(filters);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] ??= issue.message;
      setFilterErrors(next);
      filterRefs.current[String(parsed.error.issues[0]?.path[0])]?.focus();
      return;
    }
    props.onSearch({ ...props.criteria, ...filters, page: 0 });
  };

  const runDownload = async (job: Schemas["DataExportJobView"]) => {
    if (downloadingJobId) return;
    setDownloadingJobId(job.jobId); setDownloadError(undefined); setDownloadedJobId("");
    try {
      await props.onDownload(job);
      setDownloadedJobId(job.jobId);
    } catch (cause) {
      const feedback = failure(cause);
      const status = (cause as { status?: number } | null)?.status;
      setDownloadError({ job, message: feedback.message, expired: status === 410 || feedback.recovery === "new-file" });
    } finally {
      setDownloadingJobId(undefined);
    }
  };

  const columns = [
    { key: "job", title: "작업 ID", sticky: "start" as const, render: (row: Schemas["DataExportJobView"]) => props.detailHref
      ? <RowLink href={props.detailHref(row.jobId)} triggerKey={`export:${row.jobId}:detail`} onNavigate={() => props.onOpenDetail?.(row.jobId)}><code>{row.jobId}</code></RowLink>
      : <code>{row.jobId}</code> },
    { key: "type", title: "유형", render: (row: Schemas["DataExportJobView"]) => businessCodeLabel(row.exportType) },
    { key: "period", title: "정산 기간", render: (row: Schemas["DataExportJobView"]) => `${row.from} ~ ${row.to}` },
    { key: "status", title: "상태", render: (row: Schemas["DataExportJobView"]) => exportStatus(row.status) },
    { key: "requested", title: "요청일시", render: (row: Schemas["DataExportJobView"]) => date(row.requestedAt) },
    { key: "action", title: "작업", render: (row: Schemas["DataExportJobView"]) => <div className="admin-inline-actions">
      {row.status === "READY" && row.fileAssetId ? <Button aria-label={`${row.jobId} 다운로드`} disabled={Boolean(downloadingJobId)} isLoading={downloadingJobId === row.jobId} size="small" variant="quiet" onClick={() => void runDownload(row)}>다운로드</Button> : null}
      {row.status === "FAILED" ? <Button aria-label={`${row.jobId} 다시 요청`} size="small" variant="quiet" onClick={() => openRequest({ kind: "retry", source: row })}>다시 요청</Button> : null}
    </div> },
  ] satisfies readonly DataColumn<Schemas["DataExportJobView"]>[];

  const processing = props.result?.items.some((row) => ["REQUESTED", "PROCESSING"].includes(row.status)) || ["REQUESTED", "PROCESSING"].includes(props.selected?.status ?? "");

  return <>
    <PageHeader
      id={mode === "detail" ? "ADM-S32" : "ADM-S20"}
      title={mode === "detail" ? `Export 작업 ${props.selectedJobId ?? ""}` : "정산 Export"}
      description={mode === "detail" ? "정산 파일 생성 상태, 요청 조건과 다운로드 가능 여부를 확인합니다." : "내가 요청한 정산 파일의 생성 상태와 만료를 확인합니다."}
      action={mode === "detail"
        ? <Button size="small" variant="quiet" onClick={props.onRetryDetail}>최신 상태 조회</Button>
        : <div className="admin-inline-actions"><Button size="small" variant="quiet" onClick={retryList}>목록 최신 상태 조회</Button><Button size="small" onClick={() => openRequest({ kind: "new" })}>새 정산 Export 요청</Button></div>}
      breadcrumbs={mode === "detail" ? [{ label: "정산 Export", href: "/operations/exports?page=0" }, { label: "작업 상세", href: props.detailHref?.(props.selectedJobId ?? "") ?? "#" }] : undefined}
      backAction={mode === "detail" && props.backHref && props.onBack ? { label: props.backLabel ?? "목록으로 돌아가기", href: props.backHref, onClick: props.onBack } : undefined}
    />

    {props.notice ? <Alert variant="information" title="정산 Export 상태">{props.notice}</Alert> : null}
    {downloadedJobId ? <Alert variant="success" title="다운로드를 시작했습니다">{downloadedJobId} 파일을 받았습니다. 브라우저의 다운로드 목록을 확인해 주세요.</Alert> : null}
    {downloadError ? <Alert persistent variant={downloadError.expired ? "warning" : "danger"} title={downloadError.expired ? "다운로드 파일이 만료되었습니다" : "다운로드 준비 실패"} action={downloadError.expired
      ? <Button size="small" variant="quiet" onClick={() => openRequest({ kind: "retry", source: downloadError.job })}>새 Export 요청</Button>
      : <Button size="small" variant="quiet" onClick={() => void runDownload(downloadError.job)}>다시 시도</Button>}>{downloadError.message}</Alert> : null}

    {mode !== "detail" ? <><Panel title="내 정산 Export 작업 검색" description="상태·유형·요청일 기준으로 이 관리자 계정의 작업만 조회합니다.">
      <FilterBar onSubmit={submitFilters}>
        <Field compact control="select" label="작업 상태" value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.currentTarget.value as ExportStatusFilter }))}>
          <option value="">전체</option><option value="REQUESTED">요청 접수</option><option value="PROCESSING">파일 생성 중</option><option value="READY">다운로드 가능</option><option value="FAILED">생성 실패</option>
        </Field>
        <Field compact control="select" label="Export 유형" value={filters.exportType} onChange={(event) => setFilters((current) => ({ ...current, exportType: event.currentTarget.value as "" | "SETTLEMENT" }))}>
          <option value="">전체</option><option value="SETTLEMENT">정산</option>
        </Field>
        <Field ref={(element) => { filterRefs.current.requestedFrom = element; }} compact label="요청 시작일" type="date" value={filters.requestedFrom} error={filterErrors.requestedFrom} onChange={(event) => { setFilters((current) => ({ ...current, requestedFrom: event.currentTarget.value })); setFilterErrors((current) => ({ ...current, requestedFrom: "" })); }} />
        <Field ref={(element) => { filterRefs.current.requestedTo = element; }} compact label="요청 종료일" type="date" value={filters.requestedTo} error={filterErrors.requestedTo} onChange={(event) => { setFilters((current) => ({ ...current, requestedTo: event.currentTarget.value })); setFilterErrors((current) => ({ ...current, requestedTo: "" })); }} />
      </FilterBar>
    </Panel>

    <Panel title="내 정산 Export 작업" description={props.result ? `서버 기준 총 ${props.result.totalCount.toLocaleString("ko-KR")}건` : "이 관리자 계정의 요청 이력을 불러옵니다."}>
      <DataTable caption="내 정산 Export 작업" columns={columns} rows={props.result?.items ?? []} rowKey={(row) => row.jobId} state={props.listState} onRetry={retryList} />
      {props.result ? <Pagination page={props.result.page} size={props.result.size} totalCount={props.result.totalCount} onPageChange={props.onPageChange} /> : null}
    </Panel></> : null}

    {processing ? <Alert persistent variant="information" title="파일 생성 중">요청 접수와 파일 준비 완료는 별도 상태입니다. 새 요청을 만들지 말고 현재 작업의 최신 상태를 확인해 주세요.</Alert> : null}
    {props.pollTimedOut ? <Alert persistent variant="warning" title="자동 상태 확인을 멈췄습니다" action={<Button size="small" variant="quiet" onClick={props.onRetryDetail}>지금 상태 조회</Button>}>120초 동안 완료되지 않았습니다. 작업은 계속 처리될 수 있으며 이 목록에서 다시 확인할 수 있습니다.</Alert> : null}

    {mode !== "list" ? props.detailState === "loading" ? <Alert persistent variant="information" title="Export 작업을 확인하는 중입니다">선택한 작업의 최신 상태를 조회하고 있습니다.</Alert>
      : props.detailState === "denied" ? <StateNotice state="denied" />
        : props.detailState === "not-found" ? <Alert persistent variant="warning" title="내 Export 작업을 찾을 수 없습니다" action={<Button size="small" variant="quiet" onClick={() => { props.onClearSelection(); retryList(); }}>목록 다시 조회</Button>}>삭제되었거나 현재 관리자 계정의 작업이 아닙니다.</Alert>
        : props.detailState === "error" ? <Alert persistent variant="danger" title="Export 작업을 불러오지 못했습니다" action={<Button size="small" variant="quiet" onClick={props.onRetryDetail}>다시 조회</Button>}>목록은 유지됩니다. 선택 작업의 최신 상태를 다시 확인해 주세요.</Alert>
          : props.selected ? <Panel title={`Export 작업 ${props.selected.jobId}`} description={date(props.selected.requestedAt)} action={<div className="admin-inline-actions">
            {props.selected.status === "READY" && props.selected.fileAssetId ? <Button disabled={Boolean(downloadingJobId)} isLoading={downloadingJobId === props.selected.jobId} size="small" onClick={() => void runDownload(props.selected!)}>파일 다운로드</Button> : null}
            {props.selected.status === "FAILED" ? <Button size="small" variant="secondary" onClick={() => openRequest({ kind: "retry", source: props.selected })}>이 조건으로 다시 요청</Button> : null}
          </div>}><DetailList items={[
            { label: "상태", value: exportStatus(props.selected.status) },
            { label: "유형", value: businessCodeLabel(props.selected.exportType) },
            { label: "정산 기간", value: `${props.selected.from} ~ ${props.selected.to}` },
            { label: "요청 사유", value: props.selected.reason },
            { label: "파일 ID", value: props.selected.fileAssetId ?? "준비 전" },
            { label: "실패 코드", value: props.selected.failureCode || "—" },
            { label: "완료일시", value: date(props.selected.completedAt) },
          ]} />{props.selected.status === "FAILED" ? <Alert persistent variant="danger" title="정산 파일을 생성하지 못했습니다">실패 코드를 확인하고 같은 기간·사유로 새 작업을 요청할 수 있습니다.</Alert> : null}</Panel> : null : null}

    <Dialog open={Boolean(dialog)} closeDisabled={requestPending} onClose={closeRequest} size="medium" title={dialog?.kind === "retry" ? "같은 조건으로 새 Export를 요청할까요?" : "정산 Export를 요청할까요?"} description="요청이 접수된 뒤 별도 작업에서 파일을 생성합니다."
      secondaryAction={<Button disabled={requestPending} size="small" variant="quiet" onClick={closeRequest}>취소</Button>}
      primaryAction={<GuidedActionButton blockedReason={requestBlocked ? "직전 파일 생성 요청의 결과가 확정되지 않았습니다. 내 작업 다시 조회를 눌러 확인해 주세요." : null} form={formId} isLoading={requestPending} size="small" type="submit">파일 생성 요청</GuidedActionButton>}>
      {requestError ? <Alert persistent variant="danger" title={requestBlocked ? "처리 상태 확인 필요" : "요청 실패"} action={requestBlocked ? <Button size="small" variant="quiet" onClick={() => void reconcileRequest()}>내 작업 다시 조회</Button> : undefined}>{requestError}</Alert> : null}
      <form className="admin-command" id={formId} noValidate onSubmit={(event) => { event.preventDefault(); void submitRequest(); }}>
        <Field ref={(element) => { requestRefs.current.from = element; }} compact label="정산 시작일" required type="date" value={draft.from} error={requestErrors.from} onChange={(event) => updateDraft("from", event.currentTarget.value)} />
        <Field ref={(element) => { requestRefs.current.to = element; }} compact label="정산 종료일" required type="date" value={draft.to} error={requestErrors.to} onChange={(event) => updateDraft("to", event.currentTarget.value)} />
        <Field ref={(element) => { requestRefs.current.reason = element; }} compact control="textarea" label="요청 사유" required maxLength={500} value={draft.reason} error={requestErrors.reason} onChange={(event) => updateDraft("reason", event.currentTarget.value)} />
      </form>
    </Dialog>
  </>;
}
